var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import Autocomplete from '@trevoreyre/autocomplete-js';
import { customElement, state } from 'lit/decorators.js';
import { b2bSearch } from '../../apis/api-b2b.js';
import { getLanguageCode } from '../../i18n.js';
import { crossSvg, divider, searchSvg } from '../../helpers/elements.js';
import { replaceAll } from '../../helpers/string.js';
import { ElementB2bSearchResult } from './element-b2b-search-result.js';
import { createStaticPageUrl } from '../../urlHandling/urlCreate.js';
import { Router } from '@vaadin/router';
import { sendAnalytics } from '../../analytics.js';
import { setFileTitleParam } from '../../urlHandling/urlSearchParams.js';
let ElementB2bSearch = class ElementB2bSearch extends LitElement {
    constructor() {
        super(...arguments);
        this.counter = 0;
        this.inputValue = '';
        this.showAll = false;
        this.analyticsSearchTimeout = undefined;
    }
    firstUpdated() {
        this.autocomplete = new Autocomplete(this, {
            debounceTime: 200,
            search: (input) => {
                return new Promise((resolve) => {
                    if (input.length >= 1) {
                        if (this.analyticsSearchTimeout) {
                            clearTimeout(this.analyticsSearchTimeout);
                        }
                        this.analyticsSearchTimeout = setTimeout(() => {
                            sendAnalytics('event', 'search_query', {
                                event_label: this.inputValue,
                            });
                        }, 2000);
                        b2bSearch(input, getLanguageCode()).then((results) => {
                            this.counter = results.length;
                            resolve(results);
                        });
                    }
                    else {
                        resolve([]);
                    }
                });
            },
            renderResult: (result, props) => {
                if (props['data-result-index'] > 7 && !this.showAll)
                    return '';
                const resultElem = new ElementB2bSearchResult();
                resultElem.result = result;
                resultElem.parent = this;
                resultElem.props = props;
                resultElem.className = 'col-span-10 xxl:col-span-8';
                resultElem.addEventListener('', () => this.onShowAll());
                resultElem.addEventListener('', () => this.onShowLess());
                return resultElem;
            },
            getResultValue: (result) => {
                return replaceAll(replaceAll(result.title, '<b>', ''), '</b>', '');
            },
            onSubmit: (result) => {
                if (!result)
                    return;
                sendAnalytics('event', 'search_select', {
                    event_label: `${result.pageTitleShort}`,
                });
                const lang = getLanguageCode();
                const params = result.fileSize
                    ? setFileTitleParam(result.title)
                    : undefined;
                Router.go(createStaticPageUrl(lang, result.pageTitleShort, params));
                this.closeSearch();
            },
        });
        this.autocomplete.input.removeEventListener('blur', this.autocomplete.core.handleBlur);
        this.autocomplete.input.focus();
        this.addEventListener('keydown', (e) => {
            if (e.code === 'Escape')
                this.closeSearch();
        });
        this.querySelector('input').addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Tab')
                this.autocomplete.core.updateResults(this.autocomplete.input.value);
        });
    }
    onShowAll() {
        this.showAll = true;
        this.autocomplete.core.updateResults(this.autocomplete.input.value);
    }
    onShowLess() {
        this.showAll = false;
        this.autocomplete.core.updateResults(this.autocomplete.input.value);
    }
    closeSearch() {
        this.dispatchEvent(new CustomEvent('close'));
    }
    inputChanged(e) {
        this.inputValue = e.target.value;
    }
    resetInputField() {
        this.querySelector('input').value = '';
        this.autocomplete.core.updateResults(this.autocomplete.input.value);
        this.inputValue = '';
    }
    connectedCallback() {
        if (this.autocomplete?.input)
            this.autocomplete.input.focus();
        super.connectedCallback();
    }
    render() {
        return html `
      <div class="w-full shadow-search bg-chm-tertiary-gray">
        <div class="w-content mx-auto grid grid-cols-10 gap-x-4">
          <div class="col-span-10 flex h-[80px] items-center justify-center">
            ${searchSvg('h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l mr-[22px] shrink-0')}
            <input
              type="text"
              class="field-font h-[56px] min-w-[75px] flex-auto shrink p-4 outline-none"
              placeholder="Suchen..."
              @input="${(e) => this.inputChanged(e)}"
            />
            <button
              class="-ml-[32px] mr-[16px] md:-ml-[40px] md:mr-[20px]"
              @click="${() => this.resetInputField()}"
            >
              ${crossSvg('h-logo-chm-plus md:h-header-icon-s fill-chm-red hover:fill-chm-focus-red cursor-pointer')}
            </button>
            <button class="ml-[20px]" @click="${() => this.closeSearch()}">
              ${crossSvg('h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l fill-chm-red hover:fill-chm-focus-red cursor-pointer')}
            </button>
          </div>
          ${divider('100vw', '1px', '0 0', '#DEE2E6', 'absolute z-50 left-0 top-[80px]')}
          <div class="w-content z-50">
            <ul
              class="grid max-h-[70vh] !w-[inherit] max-w-[inherit] grid-cols-10 overflow-y-auto bg-chm-tertiary-gray shadow-search"
            ></ul>
          </div>
        </div>
      </div>
    `;
    }
    createRenderRoot() {
        return this;
    }
};
__decorate([
    state()
], ElementB2bSearch.prototype, "counter", void 0);
__decorate([
    state()
], ElementB2bSearch.prototype, "inputValue", void 0);
__decorate([
    state()
], ElementB2bSearch.prototype, "showAll", void 0);
ElementB2bSearch = __decorate([
    customElement('element-b2b-search')
], ElementB2bSearch);
export { ElementB2bSearch };
//# sourceMappingURL=element-b2b-search.js.map